import { useRequiredContext } from "@redotech/react-util/context";
import { filterTruthy } from "@redotech/util/array";
import { deepIntersectionEquals } from "@redotech/util/equal";
import { getOrSet } from "@redotech/util/map";
import { sinkValue } from "@redotech/util/type";
import {
  Context,
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

export interface TreeNodeEntry<T = any> {
  id: string;
  data: T;
  parentId: string | null;
}

export interface HydratedTreeNode<T = any> extends TreeNodeEntry<T> {
  children: HydratedTreeNode<T>[];
}

interface TreeContextValue<T = any> {
  registerNode: (id: string, data: T, parentId?: string) => void;
  unregisterNode: (id: string) => void;
  getNode: (id: string) => TreeNodeEntry<T> | undefined;
  trees: HydratedTreeNode<T>[];
  updateNodeData: (id: string, data: T) => void;
}

const TreeContext = createContext<TreeContextValue | undefined>(undefined);

export interface TreeProviderProps {
  children: ReactNode;
}

export const TreeProvider = memo(function TreeProvider({
  children,
}: TreeProviderProps) {
  const [nodeIdToNode] = useState(() => new Map<string, TreeNodeEntry>());
  const [parentIdToChildren] = useState(() => new Map<string, Set<string>>());
  const [version, setVersion] = useState(0);

  const registerNode = useCallback(
    (id: string, data: any, parentId?: string) => {
      // Check if node already exists to avoid duplicate registration
      if (nodeIdToNode.has(id)) {
        return;
      }

      if (parentId) {
        getOrSet(parentIdToChildren, parentId, () => new Set()).add(id);
      }

      const node: TreeNodeEntry = { id, data, parentId: parentId ?? null };

      nodeIdToNode.set(id, node);

      setVersion((v) => v + 1);
    },
    [nodeIdToNode, parentIdToChildren],
  );

  const unregisterNode = useCallback(
    (id: string) => {
      const node = nodeIdToNode.get(id);
      if (!node) return;

      const removeDescendants = (n: string) => {
        const children = parentIdToChildren.get(n) || new Set();
        for (const child of Array.from(children)) {
          removeDescendants(child);
        }
        nodeIdToNode.delete(n);
        parentIdToChildren.delete(n);
      };

      removeDescendants(node.id);
      setVersion((v) => v + 1);
    },
    [nodeIdToNode, parentIdToChildren],
  );

  const getNode = useCallback(
    (id: string) => {
      sinkValue(version);
      return nodeIdToNode.get(id);
    },
    [nodeIdToNode, version],
  );

  const trees: HydratedTreeNode[] = useMemo(() => {
    sinkValue(version);
    const buildTree = (node: TreeNodeEntry): HydratedTreeNode => {
      const childrenIds = parentIdToChildren.get(node.id) || new Set();
      const children = filterTruthy(
        Array.from(childrenIds).map((childId) => nodeIdToNode.get(childId)),
      ).map(buildTree);

      return { ...node, children };
    };

    const rootNodes = Array.from(nodeIdToNode.values()).filter(
      (node) => !node.parentId,
    );

    return rootNodes.map(buildTree);
  }, [nodeIdToNode, version, parentIdToChildren]);

  const updateNodeData = useCallback(
    (id: string, data: any) => {
      const node = nodeIdToNode.get(id);
      if (node) {
        node.data = data;
        setVersion((v) => v + 1);
      }
    },
    [nodeIdToNode],
  );

  const value = useMemo(
    () => ({ registerNode, unregisterNode, getNode, trees, updateNodeData }),
    [registerNode, unregisterNode, getNode, trees, updateNodeData],
  );

  return <TreeContext.Provider value={value}>{children}</TreeContext.Provider>;
});

export function useTreeContext<T>(): TreeContextValue<T> {
  const context = useRequiredContext(TreeContext);
  return context as TreeContextValue<T>;
}

export const TreeNodeContext = createContext<string | undefined>(undefined);

export function useCurrentTreeNodeId(): string | undefined {
  return useContext(TreeNodeContext);
}

export interface UseTreeNodeOptions<T = any> {
  data: T;
  skipRegistration?: boolean;
  dataEquals?: (a: T, b: T) => boolean;
}

export function useTreeNode<T = any>({
  data,
  skipRegistration = false,
  dataEquals = deepIntersectionEquals,
}: UseTreeNodeOptions<T>): {
  id: string;
  node: TreeNodeEntry<T> | undefined;
  parentId: string | undefined;
  TreeContext: Context<string | undefined>;
} {
  const id = useId();
  const parentId = useCurrentTreeNodeId();
  const treeContext = useContext(TreeContext);
  const previousDataRef = useRef<T>(data);
  const registrationCompleteRef = useRef(false);

  useEffect(() => {
    if (skipRegistration || !treeContext) return;

    const { registerNode, unregisterNode } = treeContext;
    registerNode(id, data, parentId);
    registrationCompleteRef.current = true;

    return () => {
      registrationCompleteRef.current = false;
      unregisterNode(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, parentId, skipRegistration]);

  useEffect(() => {
    if (skipRegistration || !registrationCompleteRef.current || !treeContext)
      return;

    if (!dataEquals(previousDataRef.current, data)) {
      treeContext.updateNodeData(id, data);
      previousDataRef.current = data;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, id, skipRegistration]);

  // Don't try to get the node - it causes re-renders. The node data is passed in anyway.
  return { id, parentId, node: undefined, TreeContext: TreeNodeContext };
}
